'use client';

import FactCheckIcon from '@mui/icons-material/FactCheck';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import PlaceIcon from '@mui/icons-material/Place';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Box, Card, CardContent, CircularProgress, Grid, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import BarGraph from '../graph/BarGraph';
import PieChart from '../graph/PieChart';
import {
  InventoryAuditorDashboardPayload,
  useInventoryAuditorDashboard,
} from '@/hooks/useInventoryAuditorDashboard';

const MOVEMENT_COLORS: Record<string, string> = {
  site_visit: 'rgba(46, 125, 50, 0.85)',
  adjustment: 'rgba(237, 108, 2, 0.85)',
  other: 'rgba(97, 97, 97, 0.75)',
};

function formatShortDate(ymd: string): string {
  const [, m, d] = ymd.split('-');
  return `${parseInt(m, 10)}/${parseInt(d, 10)}`;
}

function iaReasonLabel(reason: string): string {
  if (reason === 'site_visit') return 'Store count';
  if (reason === 'adjustment') return 'Adjustment';
  return 'Other';
}

function StatCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
}) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ color: 'primary.main', mt: 0.25 }}>{icon}</Box>
          <Box>
            <Typography variant="h5" component="p" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function ChartsBody({ data }: { data: InventoryAuditorDashboardPayload }) {
  const movementBarData = useMemo(() => {
    const labels = data.movementsByDay.map((r) => formatShortDate(r.date));
    const keys = ['siteVisitUnits', 'adjustmentUnits', 'otherUnits'] as const;
    const keyToReason: Record<(typeof keys)[number], keyof typeof MOVEMENT_COLORS> = {
      siteVisitUnits: 'site_visit',
      adjustmentUnits: 'adjustment',
      otherUnits: 'other',
    };
    const labelForKey = (k: (typeof keys)[number]): string => {
      if (k === 'siteVisitUnits') return 'Store count';
      if (k === 'adjustmentUnits') return 'Adjustment';
      return 'Other';
    };
    return {
      labels,
      datasets: keys.map((key) => ({
        label: labelForKey(key),
        data: data.movementsByDay.map((r) => r[key]),
        backgroundColor: MOVEMENT_COLORS[keyToReason[key]],
        stack: 'movements',
      })),
    };
  }, [data.movementsByDay]);

  const storeCountBarData = useMemo(
    () => ({
      labels: data.storeCountMovesByDay.map((r) => formatShortDate(r.date)),
      datasets: [
        {
          label: 'Store count movements',
          data: data.storeCountMovesByDay.map((r) => r.count),
          backgroundColor: 'rgba(25, 118, 210, 0.7)',
        },
      ],
    }),
    [data.storeCountMovesByDay],
  );

  const inventoryBarData = useMemo(
    () => ({
      labels: data.inventoryByLocation.map((r) => r.locationName),
      datasets: [
        {
          label: 'Units on hand',
          data: data.inventoryByLocation.map((r) => r.totalUnits),
          backgroundColor: 'rgba(46, 125, 50, 0.75)',
        },
      ],
    }),
    [data.inventoryByLocation],
  );

  const reasonPieData = useMemo(() => {
    const filtered = data.movementsByReason.filter((r) => r.totalQuantity > 0);
    return {
      labels: filtered.map((r) => iaReasonLabel(r.reason)),
      datasets: [
        {
          data: filtered.map((r) => r.totalQuantity),
          backgroundColor: filtered.map((r) => MOVEMENT_COLORS[r.reason] ?? MOVEMENT_COLORS.other),
        },
      ],
    };
  }, [data.movementsByReason]);

  const rangeLabel = `${data.range.dateFrom} — ${data.range.dateTo}`;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Last {data.range.days} days ({rangeLabel})
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard
            label="Units on hand"
            value={data.summary.totalUnitsOnHand.toLocaleString()}
            icon={<Inventory2Icon />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard
            label="SKUs in stock"
            value={data.summary.skusWithStock.toLocaleString()}
            icon={<StorefrontIcon />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard
            label="Catalog items"
            value={data.summary.catalogItemCount.toLocaleString()}
            icon={<Inventory2Icon fontSize="small" />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard
            label="Active locations"
            value={data.summary.activeLocations}
            icon={<PlaceIcon />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard
            label="Movements (period)"
            value={data.summary.movementCountInRange.toLocaleString()}
            icon={<SwapHorizIcon />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <StatCard
            label="Store count moves"
            value={data.summary.storeCountMovementCountInRange.toLocaleString()}
            icon={<FactCheckIcon />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Inventory movement (units)
              </Typography>
              <BarGraph uuid="ia-movements-by-day" chartData={movementBarData} height={260} stacked />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Movement by reason
              </Typography>
              {reasonPieData.labels.length > 0 ? (
                <PieChart uuid="ia-movements-reason" chartData={reasonPieData} height={240} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No movements in this period.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Store count activity (movements per day)
              </Typography>
              <BarGraph uuid="ia-store-count-moves" chartData={storeCountBarData} height={220} />
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Inventory by location (top sites)
              </Typography>
              {inventoryBarData.labels.length > 0 ? (
                <BarGraph uuid="ia-inventory-location" chartData={inventoryBarData} height={220} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No location stock recorded yet.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Adjustment movements (period): {data.summary.adjustmentMovementCountInRange.toLocaleString()}
      </Typography>
    </Box>
  );
}

const InventoryAuditorDashboardCharts: React.FC = () => {
  const { data, isLoading, error } = useInventoryAuditorDashboard(30);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Typography color="error" variant="body2">
        Could not load dashboard metrics.
      </Typography>
    );
  }

  return <ChartsBody data={data} />;
};

export default InventoryAuditorDashboardCharts;
